import React, { useState, useEffect } from 'react';
import { Input, Tooltip } from 'antd';
import {
  CheckCircleIcon,
  InformationCircleIcon,
  PlusIcon,
  SearchIcon,
} from '@heroicons/react/outline';
import { CreditCard } from 'react-kawaii';
import InfiniteScroll from 'react-infinite-scroll-component';
import DefaultLayout from '../../components/layout/DefaultLayout';
import Card from '../../components/cards/card';
import { Link } from 'react-router-dom';
import cardsService from '../../services/cards';
import debounceAsync from '../../helpers/debounce';
import CardSkeleton from '../../components/cards/card-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCards, fetchCardsNextPage } from '../../store/actions/cards';

const HomeScreen = () => {
  const dispatch = useDispatch();
  const { items, loading, currentPage, totalPages } = useSelector(
    (state) => state.cards
  );

  const [filteredCards, setFilteredCards] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(fetchCards());
    // eslint-disable-next-line
  }, []);

  // eslint-disable-next-line
  useEffect(() => searchCard(search), [search, items]);

  async function searchCard(search) {
    if (!search) return setFilteredCards(items);

    const beginSearch = async () => {
      let filtered = [];
      filtered = items.filter((card) => {
        const name_on_card = card.name_on_card.toLowerCase();
        const code = card.code.toLowerCase();
        const signature = card.signature.toLocaleLowerCase();
        return (
          name_on_card.indexOf(search.toLocaleLowerCase()) > -1 ||
          code.indexOf(search.toLocaleLowerCase()) > -1 ||
          signature.indexOf(search.toLocaleLowerCase()) > -1
        );
      });

      if (filtered.length === 0) {
        const { items } = await cardsService.search(search);

        filtered = items;
      }

      return filtered;
    };

    const filteredCards = await debounceAsync(beginSearch);

    setFilteredCards(filteredCards);
  }

  return (
    <DefaultLayout>
      <div className="p-10">
        <div className="flex gap-2">
          <h1 className="text-3xl lg:text-4xl font-bold">Tarjetas</h1>
          <Tooltip
            title="Doble click sobre una tarjeta para ver la parte trasera."
            placement="right"
          >
            <InformationCircleIcon width={16} className="cursor-help" />
          </Tooltip>
        </div>

        <div>
          <p className="mt-2 text-lg font-medium">
            Puedes crear, visualizar o editar tarjetas.
          </p>
        </div>

        <div className="mt-4 md:flex justify-between items-center">
          <Link
            to="/card"
            className="flex items-center justify-center gap-1 text-white bg-cyan-600 hover:bg-cyan-500 hover:text-white font-bold transition mb-4 lg:mb-0 rounded"
            style={{ width: 150, height: 40 }}
          >
            Crear tarjeta <PlusIcon width={20} />
          </Link>

          <Input
            placeholder="Buscar por nombre, código o firma digital"
            size="large"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            suffix={
              <SearchIcon width={24} className="text-black text-opacity-30" />
            }
            className="w-full md:w-96 rounded"
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:flex flex-wrap justify-center gap-5 md:gap-10 my-10">
            <CardSkeleton cols={filteredCards.length} />
          </div>
        ) : (
          <div>
            {filteredCards.length === 0 && (
              <div className="w-full h-96 flex flex-col justify-center items-center text-black text-opacity-50">
                <CreditCard size={150} mood="sad" color="#d7d8d8a7" />
                <p className="text-2xl mt-2">No se encontraron tarjetas</p>
                <p>Las tarjetas creadas se mostraran aqui.</p>
              </div>
            )}

            <InfiniteScroll
              dataLength={filteredCards.length}
              next={() => {
                if (loading) return;
                dispatch(fetchCardsNextPage(currentPage));
              }}
              hasMore={currentPage < totalPages}
              className="space-y-7 md:space-y-0 md:flex flex-wrap justify-center gap-5 my-10"
              style={{ overflow: 'visible' }}
              endMessage={
                filteredCards.length > 20 && (
                  <div className="w-full py-2">
                    <div className="text-center col-span-2 order-2 text-black text-opacity-60">
                      <div className="flex items-center justify-center">
                        <CheckCircleIcon width={40} />
                      </div>
                      <h4 className="font-medium">
                        No se encontrarón mas resultados
                      </h4>
                    </div>
                  </div>
                )
              }
            >
              {filteredCards.map((card, index) => (
                <Card card={card} key={index} />
              ))}
            </InfiniteScroll>
          </div>
        )}
      </div>
    </DefaultLayout>
  );
};

export default HomeScreen;
